import { FC, useEffect, useState } from 'react';
import FileUpload from '../../component/fileUpload/fileUpload';
import { validateAndDecodeAuthToken } from '../../utils/helper';
import { UserType } from '@inplay/models';
import { FieldConfig } from '../../strategies/userPresentation/userPresentationStrategy.interface';
import { UserPresentationFactory } from '../../strategies/userPresentation/userPresentationFactory';
import { useUserDetailContext } from '../../service/context/userDetailContext';
import { isUndefined } from 'lodash';
import { Trans, useTranslation } from 'react-i18next';
import { EditProfileProps } from './profile.type';
import { renderProfileCreationForm } from './profileHelper';
import { countries } from '../../component/userModal/helper';
import AddSignature from '../../component/addSignature/addSignature';

const EditProfile: FC<EditProfileProps> = (props) => {
  const { t } = useTranslation();
  const [createFieldConfigs, setCreateFieldConfigs] = useState<FieldConfig[]>(
    [],
  );
  const { userDetail } = useUserDetailContext();
  const [errorMessage, setErrorMessage] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!userDetail) return;

    const validAuthToken = validateAndDecodeAuthToken();
    const userType = validAuthToken?.type as UserType;

    const strategy = UserPresentationFactory.getPresentationStrategy(
      userType,
      userDetail,
    );
    const createFieldConfigs: FieldConfig[] =
      strategy.getEditableFieldsForProfile(userDetail);
    setCreateFieldConfigs(createFieldConfigs);

    const initialValues = createFieldConfigs.reduce(
      (defaultConfig: Record<string, any>, config: FieldConfig) => {
        if (config.type === 'phone') {
          defaultConfig[config.id] = {
            number: config.defaultValue,
            country: countries[0],
          };
        } else if (config.defaultValue !== undefined) {
          defaultConfig[config.id] = config.defaultValue;
        }
        return defaultConfig;
      },
      {},
    );

    const formValues: Record<string, any> = { ...initialValues };

    createFieldConfigs.forEach((config: FieldConfig) => {
      if (
        !isUndefined(config.defaultValue) &&
        isUndefined(formValues[config.id])
      ) {
        formValues[config.id] = config.defaultValue;
      }
    });

    if (userDetail.profilePhoto && !formValues.profilePictureUrl) {
      formValues.profilePictureUrl = userDetail.profilePhoto;
    }

    if (userDetail?.signature && !formValues.signature) {
      formValues.signature = userDetail?.signature;
    }

    props.setFormValues(formValues);
  }, [userDetail]);

  const validateForm = () => {
    const errors: Record<string, string> = {};

    createFieldConfigs.forEach((config: FieldConfig) => {
      const fieldValue = props.formValues[config.id];

      if (config.required && !fieldValue) {
        errors[config.id] = `${config.label} is required`;
      } else if (config.validation && fieldValue) {
        const error = config.validation(fieldValue);
        if (error) errors[config.id] = error;
      }
    });

    setErrorMessage(errors);
    return Object.keys(errors).length === 0;
  };

  useEffect(() => {
    if (props.setValidator) {
      props.setValidator(() => validateForm());
    }
  }, [createFieldConfigs, props.formValues]);

  const getNestedValue = (obj: any, path: string) => {
    return path.split('.').reduce((acc, part) => acc?.[part], obj);
  };

  const visibleFields = createFieldConfigs.filter((config) => {
    if (!config.dependsOn) return true;

    const { field, value, condition } = config.dependsOn;
    const currentValue = getNestedValue(props.formValues, field);

    switch (condition) {
      case 'equals':
        return currentValue === value;
      case 'notEquals':
        return currentValue !== value;
      case 'contains':
        return Array.isArray(value)
          ? value.includes(currentValue)
          : currentValue === value;
      case 'greaterThan':
        return typeof currentValue === 'number' && currentValue > value;
      case 'lessThan':
        return typeof currentValue === 'number' && currentValue < value;
      default:
        return true;
    }
  });

  const handleChange = (field: string, value: any) => {
    const newValues = {
      ...props.formValues,
      [field]: value,
    };
    props.setFormValues(newValues);

    if (errorMessage[field]) {
      setErrorMessage((prev) => ({
        ...prev,
        [field]: '',
      }));
    }
  };

  const convertFileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = (error) => reject(error);
    });
  };

  const handleFileSelect = async (file: File) => {
    const url = URL.createObjectURL(file);
    try {
      const base64 = await convertFileToBase64(file);
      props.setFormValues((prevFormValue) => ({
        ...prevFormValue,
        profilePhoto: file,
        profilePictureUrl: url,
        profilePictureBase64: base64,
        fileName: file.name,
      }));
    } catch (error) {
      console.error('Error converting file to base64:', error);
      props.setFormValues((prevFormValue) => ({
        ...prevFormValue,
        profilePhoto: file,
        profilePictureUrl: url,
        profilePictureBase64: null,
        fileName: file.name,
      }));
    }
  };

  const handleDeselect = () => {
    props.setFormValues((prevFormValue) => ({
      ...prevFormValue,
      profilePhoto: '',
      profilePictureUrl: null,
      profilePictureBase64: null,
      fileName: null,
    }));
    setErrorMessage({});
  };

  return (
    <div>
      <FileUpload
        previewImage
        showEditProfile
        onFileSelect={handleFileSelect}
        onDeselect={handleDeselect}
        previewUrl={
          props.formValues.profilePictureUrl ||
          props.formValues.profilePictureBase64 ||
          ''
        }
        selectedFileName={props.formValues.fileName || ''}
        buttonLabel={t('onboarding.upload')}
        supportedFormatsText={t('onboarding.supportedFormatsText')}
        acceptedTypes="image/jpeg, image/png"
        maxSize={2}
      />

      <h1 className="text-sm text-[#1A1A1A] mb-5">
        <Trans i18nKey="onboarding.step3Title" />
      </h1>

      <AddSignature
        onChange={(base64) => handleChange('signature', base64)}
        height="14rem"
        width="28rem"
        initialValue={props.formValues.signature}
      />

      {/* Dynamic Fields */}
      {renderProfileCreationForm(
        visibleFields,
        props.formValues,
        handleChange,
        errorMessage,
      )}
    </div>
  );
};

export default EditProfile;
